// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useRef, type Dispatch, type ReactNode } from 'react';

import type { LightingState } from '../../../viewport/cameraViews';
import { elementAllState, elementCategoryRows, type ElementFilterState, type ElementTally } from '../../../viewport/elementFilter';
import { Checkbox } from '../../Checkbox';
import { LightSection } from './LightSection';
import { RowExpander } from './RowExpander';
import type { SettingsAction } from './settingsState';
import styles from './ModelSettings.module.css';

const COUNT = new Intl.NumberFormat('da-DK');

/** A checkbox that can also show the native indeterminate (dash) state — `Checkbox` only takes `checked`. */
function TriStateCheckbox(props: { label: string; state: 'on' | 'off' | 'partial'; onChange: (on: boolean) => void; hint?: ReactNode; disabled?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = props.state === 'partial';
  }, [props.state]);
  return (
    <Checkbox
      ref={ref}
      label={props.label}
      checked={props.state !== 'off'}
      disabled={props.disabled}
      hint={props.hint}
      onChange={(e) => props.onChange(e.target.checked)}
    />
  );
}

export interface GeometrySectionProps {
  /** `buildingElementTallies` over `GET /walls`, `/openings`, `/spaces` and `/slabs`; null while loading. */
  tallies: ElementTally[] | null;
  state: ElementFilterState;
  wireframe: boolean;
  lighting: LightingState;
  dispatch: Dispatch<SettingsAction>;
}

/**
 * Lag › Geometri (frontend-r22.27; was "Mesh" with Bygningsdele beside it):
 * the building model's elements are the 3D model. The row is the master
 * switch over every category (tri-state), its sub-rows the categories with
 * their counts (walls, slabs, roof, spaces, doors, windows, other
 * openings; the Qt client's Bygningsdele, ReUseX #98), and its settings
 * button unfolds the lighting and Trådnet. The toggles govern an imported
 * Revit/IFC model's matching categories too (`elementCategoryForImportedKind`).
 * Etage (the clipping box — Kun etage N, or Etage N og under; the plan cuts
 * the same storey) moved to the floor/storey picker over the Ground itself
 * (frontend-r22.28 round 4) — this section no longer carries it.
 */
export function GeometrySection({ tallies, state, wireframe, lighting, dispatch }: GeometrySectionProps) {
  const rows = tallies ? elementCategoryRows(tallies, state) : [];
  const allState = tallies && rows.length > 0 ? elementAllState(tallies, state) : 'off';
  const total = rows.reduce((n, r) => n + r.count, 0);

  const hint = tallies === null ? 'indlæses…' : rows.length === 0 ? 'ingen bygningsdele i sagen' : `${COUNT.format(total)} bygningsdele`;

  return (
    <>
      <RowExpander
        row={
          <TriStateCheckbox
            label="Geometri"
            state={allState}
            disabled={rows.length === 0}
            hint={hint}
            onChange={(on) =>
              dispatch({
                type: 'buildingElementAll',
                categories: rows.map((r) => r.key),
                on,
              })
            }
          />
        }
        label="Lys og trådnet for geometrien"
        sub={
          rows.length > 0 && (
            <div className={styles.subRows} role="group" aria-label="Bygningsdele">
              {rows.map((r) => (
                <Checkbox
                  key={r.key}
                  label={r.label}
                  checked={allState !== 'off' ? !state.hidden.has(r.key) : false}
                  onChange={(e) => dispatch({ type: 'buildingElement', category: r.key, on: e.target.checked })}
                  hint={COUNT.format(r.count)}
                />
              ))}
            </div>
          )
        }
      >
        <LightSection lighting={lighting} dispatch={dispatch} />
        <Checkbox label="Trådnet" checked={wireframe} onChange={(e) => dispatch({ type: 'set', patch: { wireframe: e.target.checked } })} />
      </RowExpander>
    </>
  );
}
